import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { EntityType } from 'V2/Types';
import { useApplyProvider } from 'V2/Apply';
import { CircleChevronDown, CircleChevronUp } from 'V2/Shared/Icons';
import { PhoneIcon } from '@clearcover/atlas';
import { QUOTEMAGIC_PARTNER_PHONE_NUMBER } from 'constants/partnerConfig';

import { usePartnerConfig } from 'V2/Hooks/usePartnerConfig';
import { findEntities } from 'V2/Utilities/SummaryHelpers';
import { EditableItem } from './EditableItem';
import { QuoteMagicProgressBar } from './QuoteMagicProgressBar';
import styles from './styles.module.css';
import { Logo } from 'V2/Shared/Logo';
import { env } from 'constants/env';

type ItemType = {
  title: string;
  items: EntityType[];
  getSearch: (id: string) => string;
};

export const StepHeader: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const history = useHistory();

  const {
    app: {
      status: {
        progress_data: progressData,
        insurance_application_id: insuranceAppId,
        partner: { name: _partnerName } = { name: undefined },
        attribution_data: { partner_name: _attributionPartnerName } = {},
        step_name: stepName,
      },
      summary,
    },
  } = useApplyProvider();

  const partnerName = _partnerName ?? _attributionPartnerName;

  const {
    driver: drivers,
    vehicle: vehicles,
    coverage: coverages,
  } = findEntities(summary.entities, ['driver', 'vehicle', 'coverage']);

  const headerProgress =
    progressData &&
    progressData.find(
      (p) => p.type.id === 'insurance_application' && p.id === insuranceAppId
    );

  const isQuoteMagicPartner = [
    'quotemagic',
    'mt lead media quotemagic',
  ].includes(String(partnerName ?? '').toLowerCase());

  const showProgressBar = !isQuoteMagicPartner;
  const showHeaderContent = !isQuoteMagicPartner;
  const showQuoteMagicProgressBar = isQuoteMagicPartner && !!headerProgress;
  const {
    features: { showContactPhone },
  } = usePartnerConfig();

  const entities: ItemType[] = [
    {
      title: 'Drivers',
      items: drivers,
      getSearch: (id: string) => `?edit=true&entityId=${id}`,
    },
    {
      title: 'Vehicles',
      items: vehicles,
      getSearch: (id: string) => `?edit=true&entityId=${id}`,
    },
    {
      title: 'Coverages',
      items: coverages,
      getSearch: (id: string) => `?edit=true&entityId=${id}&stepName=coverages`,
    },
  ];

  const renderContactPhone = (phoneNumber: string) => {
    const formattedPhoneNumber = phoneNumber.replace(/[^0-9]/g, '');

    return (
      <a
        href={`tel:${formattedPhoneNumber}`}
        className={styles.contactPhone}
        data-testid="partner-phone-number"
      >
        <PhoneIcon width={20} height={20} className={styles.phoneIcon} />
        <span className={styles.phoneNumber}>{phoneNumber}</span>
      </a>
    );
  };

  return (
    <div data-testid="step-header" className={styles.container}>
      <div className={styles.heading}>
        <div className={styles.logoContainer}>
          <Logo className={styles.stepHeaderLogo} useMarkOnMobile />
        </div>

        {showProgressBar && (
          <div data-testid="progress" className={styles.progress}>
            {!isOpen && (
              <div className={styles.header}>
                <div className={styles.name}>
                  {headerProgress ? headerProgress.label : 'Customer Info'}
                </div>
                <div className={styles.step}>
                  {headerProgress ? headerProgress.description : 'Step 1 of 1'}
                </div>
              </div>
            )}
          </div>
        )}

        {showContactPhone &&
          renderContactPhone(QUOTEMAGIC_PARTNER_PHONE_NUMBER)}

        {env.itsTurtkeyTime === 'true' && (
          <div className={styles.turtkeyWrapper} aria-hidden="true">
            <img
              src="/turtkey-time/turtkey-250px.png"
              alt="Happy Thanksgiving!"
              className={styles.turtkeyIcon}
            />
            <img
              src="/turtkey-time/cooked-turkey-250px.png"
              alt="Happy Thanksgiving!"
              className={styles.cookedTurtkeyIcon}
            />
          </div>
        )}
      </div>

      {showQuoteMagicProgressBar && (
        <div className={styles.progressBarContainer}>
          <QuoteMagicProgressBar
            stepName={stepName} // This allows the progress bar to update its label based on a change of step
            percentage={
              headerProgress && headerProgress.value
                ? Number(headerProgress.value) || 0
                : 0
            }
          />
        </div>
      )}

      {showHeaderContent && (
        <div className={styles.content}>
          <div
            className={isOpen ? styles.expand : styles.collapse}
            data-testid="inner-content"
          >
            <div className={styles.editItems}>
              {entities.map((entity, i) => (
                <div
                  className={styles.editEntity}
                  key={i}
                  data-testid={`entity-${i}`}
                >
                  <div className={styles.editEntityHeader}>{entity.title}</div>
                  <div className={styles.editEntityItems}>
                    {entity.items
                      // filter out any drivers without detail_uri - undisclosed additional drivers have a null detail_uri
                      .filter((item) =>
                        item.type.id === 'driver' ? item.detail_uri : true
                      )
                      .map((item, i) => (
                        <EditableItem
                          key={i}
                          disabled={!item.detail_uri || !item.editable}
                          label={item.label}
                          onClick={() => {
                            history.push({
                              search: entity.getSearch(item.id),
                            });
                          }}
                        />
                      ))}
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.bar}>
              <div className={styles.progressContainer}>
                <div
                  data-testid="progress-bar"
                  className={styles.progressInner}
                  style={{
                    width: `${
                      headerProgress && headerProgress.value
                        ? headerProgress.value
                        : 0
                    }%`,
                  }}
                />
              </div>
              <div className={styles.drawer}>
                <div>
                  <button
                    className="cc-step-header__toggle"
                    onClick={() => setIsOpen(!isOpen)}
                    data-testid="step-header-toggle"
                  >
                    {isOpen ? (
                      <CircleChevronUp alt="menu arrow" />
                    ) : (
                      <CircleChevronDown alt="menu arrow" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
